/**
 * ULD V3状态与可用性模型常量定义
 * 对应后端新的UldCoreStatusEnum和UldAvailabilityEnum
 */

// ULD核心状态枚举（V3）
export const ULD_CORE_STATUS = {
    IN_STOCK: "IN_STOCK",
    IN_TRANSIT: "IN_TRANSIT", 
    OUT_OF_SERVICE: "OUT_OF_SERVICE",
    RETIRED: "RETIRED",
    LOST: "LOST",
};

// ULD核心状态显示名称映射
export const ULD_CORE_STATUS_NAMES = {
    [ULD_CORE_STATUS.IN_STOCK]: "在库",
    [ULD_CORE_STATUS.IN_TRANSIT]: "在途",
    [ULD_CORE_STATUS.OUT_OF_SERVICE]: "停用", 
    [ULD_CORE_STATUS.RETIRED]: "退役",
    [ULD_CORE_STATUS.LOST]: "丢失",
};

// ULD核心状态颜色映射
export const ULD_CORE_STATUS_COLORS = {
    [ULD_CORE_STATUS.IN_STOCK]: "#52c41a",      // 绿色
    [ULD_CORE_STATUS.IN_TRANSIT]: "#1890ff",    // 蓝色
    [ULD_CORE_STATUS.OUT_OF_SERVICE]: "#faad14", // 黄色
    [ULD_CORE_STATUS.RETIRED]: "#ff4d4f",       // 红色
    [ULD_CORE_STATUS.LOST]: "#722ed1",          // 紫色
};

// ULD可用性枚举（V3）
export const ULD_AVAILABILITY = {
    AVAILABLE: "AVAILABLE",
    OCCUPIED: "OCCUPIED",
    UNSERVICEABLE: "UNSERVICEABLE",
};

// ULD可用性显示名称映射
export const ULD_AVAILABILITY_NAMES = {
    [ULD_AVAILABILITY.AVAILABLE]: "可用",
    [ULD_AVAILABILITY.OCCUPIED]: "占用",
    [ULD_AVAILABILITY.UNSERVICEABLE]: "不可服务",
};

// ULD可用性颜色映射
export const ULD_AVAILABILITY_COLORS = {
    [ULD_AVAILABILITY.AVAILABLE]: "#52c41a",      // 绿色
    [ULD_AVAILABILITY.OCCUPIED]: "#1890ff",       // 蓝色
    [ULD_AVAILABILITY.UNSERVICEABLE]: "#ff4d4f",  // 红色
};

// 核心状态选项（用于表单）
export const ULD_CORE_STATUS_OPTIONS = Object.keys(ULD_CORE_STATUS).map(key => ({
    value: ULD_CORE_STATUS[key],
    label: `${ULD_CORE_STATUS_NAMES[ULD_CORE_STATUS[key]]}`
}));

// 新建ULD时的核心状态选项（带业务说明）
export const NEW_ULD_CORE_STATUS_OPTIONS = [
    { 
        value: ULD_CORE_STATUS.IN_STOCK, 
        label: `${ULD_CORE_STATUS_NAMES[ULD_CORE_STATUS.IN_STOCK]}（推荐：正常入库，可立即使用）` 
    },
    { 
        value: ULD_CORE_STATUS.OUT_OF_SERVICE, 
        label: `${ULD_CORE_STATUS_NAMES[ULD_CORE_STATUS.OUT_OF_SERVICE]}（需维修检查的ULD）` 
    },
    {
        value: ULD_CORE_STATUS.IN_TRANSIT,
        label: `${ULD_CORE_STATUS_NAMES[ULD_CORE_STATUS.IN_TRANSIT]}（已分配给运输任务）`
    },
    {
        value: ULD_CORE_STATUS.RETIRED,
        label: `${ULD_CORE_STATUS_NAMES[ULD_CORE_STATUS.RETIRED]}（永久停用，不再使用）`
    },
    {
        value: ULD_CORE_STATUS.LOST,
        label: `${ULD_CORE_STATUS_NAMES[ULD_CORE_STATUS.LOST]}（补录已经确认找不到的ULD）`
    }
];

// 可用性选项（用于表单）
export const ULD_AVAILABILITY_OPTIONS = Object.keys(ULD_AVAILABILITY).map(key => ({
    value: ULD_AVAILABILITY[key],
    label: `${ULD_AVAILABILITY_NAMES[ULD_AVAILABILITY[key]]}`
}));

// 状态操作按钮配置
export const STATUS_ACTION_CONFIG = {
    // 标记停用 - 适用于IN_STOCK和IN_TRANSIT状态
    MARK_OUT_OF_SERVICE: {
        label: "标记停用",
        icon: "StopOutlined",
        targetStatus: ULD_CORE_STATUS.OUT_OF_SERVICE,
        availableFrom: [ULD_CORE_STATUS.IN_STOCK, ULD_CORE_STATUS.IN_TRANSIT],
        color: "#faad14",
        confirmMessage: (currentStatus) => {
            if (currentStatus === ULD_CORE_STATUS.IN_TRANSIT) {
                return "确认将此在途ULD标记为停用状态？（通常表示运输中损坏）";
            }
            return "确认将此ULD标记为停用状态？";
        }
    },
    
    // 修复完成 - 适用于OUT_OF_SERVICE状态
    REPAIR_COMPLETE: {
        label: "修复完成",
        icon: "PlayCircleOutlined",
        targetStatus: ULD_CORE_STATUS.IN_STOCK,
        availableFrom: [ULD_CORE_STATUS.OUT_OF_SERVICE],
        color: "#52c41a",
        confirmMessage: "确认此ULD修复完成，恢复为在库状态？"
    },
    
    // 恢复在库 - 适用于RETIRED状态（数据纠错）
    RESTORE_IN_STOCK: {
        label: "恢复在库",
        icon: "UndoOutlined",
        targetStatus: ULD_CORE_STATUS.IN_STOCK,
        availableFrom: [ULD_CORE_STATUS.RETIRED],
        color: "#52c41a",
        confirmMessage: "确认将此退役ULD恢复为在库状态？（通常用于数据纠错）"
    },

    // 标记退役 - 适用于IN_STOCK和OUT_OF_SERVICE状态（在途不再用退役表示丢失，运丢了点「标记丢失」）
    MARK_RETIRED: {
        label: "标记退役",
        icon: "DeleteOutlined",
        targetStatus: ULD_CORE_STATUS.RETIRED,
        availableFrom: [ULD_CORE_STATUS.IN_STOCK, ULD_CORE_STATUS.OUT_OF_SERVICE],
        color: "#ff4d4f",
        confirmMessage: "确认将此ULD标记为退役状态？退役表示寿命结束（报废、退租、不再用），此操作通常不可逆。",
        isDangerous: true
    },

    // 标记丢失 - 适用于IN_STOCK、IN_TRANSIT和OUT_OF_SERVICE状态（原因必填）
    MARK_LOST: {
        label: "标记丢失",
        icon: "WarningOutlined",
        targetStatus: ULD_CORE_STATUS.LOST,
        availableFrom: [ULD_CORE_STATUS.IN_STOCK, ULD_CORE_STATUS.IN_TRANSIT, ULD_CORE_STATUS.OUT_OF_SERVICE],
        color: "#722ed1",
        confirmMessage: "确认已找不到此ULD？标记后可用性变为不可用，需填写原因。",
        needsReason: true,
        isDangerous: true
    },

    // 找回 - 适用于LOST状态，必须选择去向、填原因并确认在库类地点
    RECOVER: {
        label: "找回",
        icon: "RollbackOutlined",
        availableFrom: [ULD_CORE_STATUS.LOST],
        color: "#52c41a",
        isRecover: true
    }
};

// 根据当前状态获取可用的操作
export const getAvailableActions = (currentStatus) => {
    return Object.entries(STATUS_ACTION_CONFIG)
        .filter(([_, config]) => config.availableFrom.includes(currentStatus))
        .map(([key, config]) => ({ key, ...config }));
};

// 状态转换规则验证
export const validateStatusTransition = (fromStatus, toStatus) => {
    const transitions = {
        [ULD_CORE_STATUS.IN_STOCK]: [
            ULD_CORE_STATUS.IN_TRANSIT,     // 通过位置更新
            ULD_CORE_STATUS.OUT_OF_SERVICE, // 标记停用
            ULD_CORE_STATUS.RETIRED,        // 标记退役
            ULD_CORE_STATUS.LOST,           // 标记丢失
        ],
        [ULD_CORE_STATUS.IN_TRANSIT]: [
            ULD_CORE_STATUS.IN_STOCK,       // 通过位置更新
            ULD_CORE_STATUS.OUT_OF_SERVICE, // 运输中损坏
            ULD_CORE_STATUS.LOST,           // 运输中丢失（不再用退役表示）
        ],
        [ULD_CORE_STATUS.OUT_OF_SERVICE]: [
            ULD_CORE_STATUS.IN_STOCK,       // 修复完成
            ULD_CORE_STATUS.RETIRED,        // 标记退役
            ULD_CORE_STATUS.LOST,           // 标记丢失
        ],
        [ULD_CORE_STATUS.RETIRED]: [
            ULD_CORE_STATUS.IN_STOCK,  // 数据纠错：恢复在库
        ],
        [ULD_CORE_STATUS.LOST]: [
            ULD_CORE_STATUS.IN_STOCK,       // 找回完好（须同时确认在库类地点）
            ULD_CORE_STATUS.OUT_OF_SERVICE, // 找回损坏（须同时确认在库类地点）
        ],
    };
    
    return transitions[fromStatus]?.includes(toStatus) || false;
};

/**
 * 普通改位置是否被当前状态拦住。返回提示文案；null 表示可以改。
 * 丢失须先找回；停用/退役与现有批量改位置前置检查同一口径；外放须先撤销。
 */
export const getLocationUpdateBlockReason = (coreStatus, isDelivered, consigneeName) => {
    if (isDelivered) {
        return `ULD已外放给${consigneeName || "收货人"}，请先撤销外放再修改位置`;
    }
    if (coreStatus === ULD_CORE_STATUS.LOST) {
        return "ULD当前状态为丢失，无法更新位置，请先找回";
    }
    if (coreStatus === ULD_CORE_STATUS.OUT_OF_SERVICE) {
        return "ULD当前状态为停用，无法更新位置";
    }
    if (coreStatus === ULD_CORE_STATUS.RETIRED) {
        return "ULD当前状态为退役，无法更新位置";
    }
    return null;
};
